

import { useEffect, useState, useRef } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

interface UseMicrophoneProps {
  speechToken: string | null; 
  serviceEndpoint: string | null; 
  language: string;
}

const useMicrophone = ({
  speechToken,
  serviceEndpoint,
  language,
}: UseMicrophoneProps) => {
  const [finalTranscript, setFinalTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);

  useEffect(() => {
    if (!speechToken) {
      setError("Azure Speech SDK token or endpoint is missing.");
      return;
    }
    else{
      setError(null)
    }
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync();
      recognizerRef.current.close();
      recognizerRef.current = null;
    }

    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        speechToken,
        "custom"
      );

      speechConfig.setProperty(
        SpeechSDK.PropertyId.SpeechServiceConnection_Endpoint,
        `https://ponsspeech.cognitiveservices.azure.com/speech/recognition/conversation/cognitiveservices/v1`
      );

      speechConfig.speechRecognitionLanguage = language;
      speechConfig.enableDictation(); 

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      recognizerRef.current = recognizer;

      recognizer.recognizing = (s, e) => {
        setInterimTranscript(e.result.text);
      };

      recognizer.recognized = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          setFinalTranscript((prev) => `${prev} ${e.result.text}`);
          setInterimTranscript(""); 
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
        }
      };

      recognizer.canceled = (s, e) => {
        console.error(`Canceled: Reason=${e.reason}`);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error(`Error details: ${e.errorDetails}`);
          setError(`Speech recognition error: ${e.errorDetails}`);
        }
        stopListening(); 
      };

      recognizer.sessionStopped = (s, e) => {
        stopListening(); 
      };
    } catch (err) {
      console.error("Error initializing SpeechRecognizer:", err);
      setError("Failed to initialize Speech Recognition.");
    }

    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.close();
        recognizerRef.current = null;
      }
    };
  }, [speechToken, serviceEndpoint, language]); 

  const startListening = () => {
    if (error) {
      console.warn("Cannot start listening due to an error:", error);
      return;
    }

    setFinalTranscript("");
    setInterimTranscript("");
    setError(null);
    setIsListening(true);

    if (recognizerRef.current) {
      recognizerRef.current.startContinuousRecognitionAsync(
        () => {},
        (err) => {
          console.error("Failed to start recognition.", err);
          setError("Failed to start speech recognition.");
          setIsListening(false);
        }
      );
    } else {
      setError("Speech recognizer not initialized.");
    }
  };

  const stopListening = () => {
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          setIsListening(false);
          setInterimTranscript("");
        },
        (err) => {
          console.error("Failed to stop recognition.", err);
          setError("Failed to stop speech recognition.");
          setIsListening(false);
        }
      );
    }
  };

  return {
    finalTranscript,
    interimTranscript,
    isListening,
    startListening,
    stopListening,
    error,
  };
};

export default useMicrophone;